import { useCallback, useEffect, useMemo, useState } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import {
  SiteSelect,
  StatusBadge,
  type WorkflowTab,
  WorkflowTabBar,
} from '@/components/common';
import { AccountsCombobox } from '@/components/common/accounts-combobox';
import { Button } from '@/components/ui/button';
import { ConfirmProvider, useConfirm } from '@/components/ui/confirm-dialog';
import { SelectableCardGrid } from '@/components/ui/selectable-card-grid';
import { toast } from '@/components/ui/toast';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useAccessToken } from '@/services/auth';
import {
  fromRecordKey,
  ON_FARM_IMPLANT_SOURCE_SEQUENCE,
  type OnFarmImplantEmbryoPick,
  OnFarmImplantRequestError,
  onFarmImplantSelectionOptions,
  type OnFarmImplantSession,
  onFarmImplantSessionOptions,
  onFarmImplantSessionsOptions,
  type OnFarmImplantSessionSource,
  type OnFarmImplantShipmentContents,
  type OnFarmImplantShipmentSummary,
  type SaveSelectionRequest,
  toApiSourceType,
  toRecordKey,
  toUiSourceType,
  useClearOnFarmImplantSelection,
  useSaveOnFarmImplantSelection,
  useStartOnFarmImplantSession,
  useUpdateOnFarmImplantSession,
} from '@/services/on-farm-implant';
import { formatShortSessionId } from '@/utils/formatting';

import { ClientOnFarmTab } from './components/ClientOnFarmTab';
import { LoadedSourceCard } from './components/EntryPage/LoadedSourceCard';
import { FreshTab } from './components/FreshTab';
import { FrozenTab } from './components/FrozenTab';
import { ImplantQueueTab } from './components/ImplantQueueTab';
import { NonTogTab } from './components/NonTogTab';
import { OnFarmFreezingTab } from './components/OnFarmFreezingTab';
import { PregCheckTab } from './components/PregCheckTab';
import { QcTab } from './components/QcTab';
import { useOnFarmImplantSources } from './hooks/useOnFarmImplantSources';
import { useSessionEvents } from './hooks/useSessionEvents';
import type { EmbryoSource, ImplantSessionStatus, LoadedSource } from './types';
import {
  addFreshShipment,
  addShipmentEmbryoPicks,
  buildFrozenShipmentsFromPicks,
  freshShipmentsInSelection,
  isSameShipment,
  type PickedShipment,
  readStoredFreshShipments,
  removeFreshShipment,
  removeShipmentEmbryoPicks,
  type ShipmentAddResult,
  type ShipmentInSelection,
  toEmbryoPickKey,
  toEmbryoPickMatingKey,
  toPickedShipment,
  toShipmentEmbryoPicks,
  writeStoredFreshShipments,
} from './utils/shipmentSelection';

type ImplantTab =
  | 'entry'
  | 'fresh'
  | 'frozen'
  | 'flush'
  | 'client-on-farm-tog'
  | 'non-tog'
  | 'implant-queue'
  | 'qc'
  | 'on-farm-freezing'
  | 'on-farm-preg-check'
  | 'exports'
  | 'uploads';

const LOADED_PREFIX = 'loaded-';

const MANUAL_ENTRY_SOURCE_ID = 'non-tog';

const FRESH_SOURCE_ID = 'fresh';

const FROZEN_SOURCE_ID = 'frozen';

const SHIPMENT_SOURCE_IDS = new Set([FRESH_SOURCE_ID, FROZEN_SOURCE_ID]);

const initialFrozenEmbryoPicks = (
  sources: OnFarmImplantSessionSource[],
): OnFarmImplantEmbryoPick[] =>
  sources.find(
    (source) => toUiSourceType(source.sourceType) === FROZEN_SOURCE_ID,
  )?.embryos ?? [];

const TAB_KEYS: ImplantTab[] = [
  'entry',
  'fresh',
  'frozen',
  'flush',
  'client-on-farm-tog',
  'non-tog',
  'implant-queue',
  'qc',
  'on-farm-freezing',
  'on-farm-preg-check',
  'exports',
  'uploads',
];

const TAB_ICONS: Record<ImplantTab, string> = {
  entry: 'pi pi-file-edit',
  fresh: 'pi pi-circle',
  frozen: 'pi pi-asterisk',
  flush: 'pi pi-list',
  'client-on-farm-tog': 'pi pi-box',
  'non-tog': 'pi pi-plus-circle',
  'implant-queue': 'pi pi-clock',
  qc: 'pi pi-check-circle',
  'on-farm-freezing': 'pi pi-database',
  'on-farm-preg-check': 'pi pi-heart',
  exports: 'pi pi-download',
  uploads: 'pi pi-upload',
};

const TAB_LABEL_KEYS: Record<ImplantTab, string> = {
  entry: 'onFarmImplant.tabs.entry',
  fresh: 'onFarmImplant.tabs.fresh',
  frozen: 'onFarmImplant.tabs.frozen',
  flush: 'onFarmImplant.tabs.flush',
  'client-on-farm-tog': 'onFarmImplant.tabs.clientOnFarmTog',
  'non-tog': 'onFarmImplant.tabs.nonTog',
  'implant-queue': 'onFarmImplant.tabs.implantQueue',
  qc: 'onFarmImplant.tabs.qc',
  'on-farm-freezing': 'onFarmImplant.tabs.onFarmFreezing',
  'on-farm-preg-check': 'onFarmImplant.tabs.onFarmPregCheck',
  exports: 'onFarmImplant.tabs.exports',
  uploads: 'onFarmImplant.tabs.uploads',
};

const statusColors: Record<ImplantSessionStatus, { bg: string; text: string }> =
  {
    Active: { bg: 'bg-green-100', text: 'text-green-700' },
    Completed: { bg: 'bg-blue-100', text: 'text-blue-700' },
    Draft: { bg: 'bg-gray-100', text: 'text-gray-700' },
  };

const PlaceholderTab = ({ label }: { label: string }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 flex-col items-center justify-center py-24 text-gray-400">
      <i className="pi pi-wrench mb-3 text-4xl" />
      <p className="text-base font-medium">{label}</p>
      <p className="mt-1 text-sm">
        {t('onFarmImplant.entry.underConstruction')}
      </p>
    </div>
  );
};

type EntryPageContentProps = {
  sessionsEnabled: boolean;
  sessionId: string | null;
  session: OnFarmImplantSession | undefined;
  implantSiteId: string | null;
  accountId: string | undefined;
  initialSources: OnFarmImplantSessionSource[];
  onSiteChange: (siteId: string | null) => void;
  onAccountChange: (accountId: string | undefined) => void;
  onNavigateToTab: (tab: ImplantTab) => void;
};

const EntryPageContent = ({
  sessionsEnabled,
  sessionId,
  session,
  implantSiteId,
  accountId,
  initialSources,
  onSiteChange,
  onAccountChange,
  onNavigateToTab,
}: EntryPageContentProps) => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>(() =>
    initialSources.map((source) => toUiSourceType(source.sourceType)),
  );
  const [sourceOrder, setSourceOrder] = useState<string[]>(() =>
    initialSources.map(
      (source) => `${LOADED_PREFIX}${toUiSourceType(source.sourceType)}`,
    ),
  );
  const [recordKeysBySource, setRecordKeysBySource] = useState<
    Record<string, string[]>
  >(() =>
    Object.fromEntries(
      initialSources.map((source) => [
        toUiSourceType(source.sourceType),
        source.records.map((record) =>
          toRecordKey(record.recordId, record.accountId),
        ),
      ]),
    ),
  );
  const [hasCustomOrder, setHasCustomOrder] = useState(
    () => initialSources.length > 0,
  );
  const [frozenEmbryoPicks, setFrozenEmbryoPicks] = useState<
    OnFarmImplantEmbryoPick[]
  >(() => initialFrozenEmbryoPicks(initialSources));
  const [freshShipments, setFreshShipments] = useState<PickedShipment[]>(() =>
    readStoredFreshShipments(sessionId),
  );

  useEffect(() => {
    writeStoredFreshShipments(sessionId, freshShipments);
  }, [freshShipments, sessionId]);

  const { mutateAsync: saveSelection, isPending: isSaving } =
    useSaveOnFarmImplantSelection();
  const { mutate: clearSelection } = useClearOnFarmImplantSelection();
  const { mutateAsync: startSession, isPending: isStarting } =
    useStartOnFarmImplantSession();

  const { embryoSources, loadedSources, isLoading } = useOnFarmImplantSources(
    implantSiteId,
    accountId,
  );

  const isReadOnly = sessionsEnabled && !!session && session.status !== 'Draft';

  const unavailableSourceIds = useMemo(
    () =>
      embryoSources
        .filter(
          (source) =>
            !source.manualEntry &&
            source.availableCount === 0 &&
            !SHIPMENT_SOURCE_IDS.has(source.id),
        )
        .map((source) => source.id),
    [embryoSources],
  );

  const disabledSourceIds = useMemo(
    () =>
      isReadOnly
        ? embryoSources.map((source) => source.id)
        : unavailableSourceIds,
    [embryoSources, isReadOnly, unavailableSourceIds],
  );

  const visibleLoadedSources = useMemo(() => {
    const filtered = loadedSources.filter((s) =>
      selectedSourceIds.includes(s.sourceId),
    );
    if (sourceOrder.length === 0) return filtered;
    return [...filtered].sort(
      (a, b) => sourceOrder.indexOf(a.id) - sourceOrder.indexOf(b.id),
    );
  }, [loadedSources, selectedSourceIds, sourceOrder]);

  const orderedSourceIds = useMemo(() => {
    const ordered = sourceOrder
      .map((id) => id.replace(LOADED_PREFIX, ''))
      .filter((id) => selectedSourceIds.includes(id));
    const missing = selectedSourceIds.filter((id) => !ordered.includes(id));
    return [...ordered, ...missing];
  }, [selectedSourceIds, sourceOrder]);

  const handleSelectionChange = useCallback(
    (sourceIds: string[]) => {
      setSelectedSourceIds(sourceIds);
      setSourceOrder((prev) => {
        const kept = hasCustomOrder
          ? prev.filter((id) =>
              sourceIds.includes(id.replace(LOADED_PREFIX, '')),
            )
          : [];
        const added = ON_FARM_IMPLANT_SOURCE_SEQUENCE.filter(
          (sourceId) =>
            sourceIds.includes(sourceId) &&
            !kept.includes(`${LOADED_PREFIX}${sourceId}`),
        ).map((sourceId) => `${LOADED_PREFIX}${sourceId}`);
        return [...kept, ...added];
      });
      setRecordKeysBySource((prev) => {
        const next: Record<string, string[]> = {};
        sourceIds.forEach((sourceId) => {
          if (prev[sourceId]) {
            next[sourceId] = prev[sourceId];
          }
        });
        return next;
      });
      if (!sourceIds.includes(FROZEN_SOURCE_ID)) {
        setFrozenEmbryoPicks((prev) => (prev.length === 0 ? prev : []));
      }
      if (!sourceIds.includes(FRESH_SOURCE_ID)) {
        setFreshShipments((prev) => (prev.length === 0 ? prev : []));
      }
    },
    [hasCustomOrder],
  );

  const frozenShipments = useMemo(
    () => buildFrozenShipmentsFromPicks(frozenEmbryoPicks),
    [frozenEmbryoPicks],
  );

  const shipmentsBySource = useMemo<Record<string, ShipmentInSelection[]>>(
    () => ({
      [FRESH_SOURCE_ID]: freshShipmentsInSelection(
        freshShipments,
        new Set(recordKeysBySource[FRESH_SOURCE_ID] ?? []),
      ),
      [FROZEN_SOURCE_ID]: frozenShipments.map((shipment) => ({
        ...shipment,
        selectedCount: shipment.totalEmbryos,
      })),
    }),
    [freshShipments, frozenShipments, recordKeysBySource],
  );

  const handleAddShipment = useCallback(
    (
      summary: OnFarmImplantShipmentSummary,
      contents: OnFarmImplantShipmentContents,
    ): ShipmentAddResult => {
      const shipment = toPickedShipment(summary, contents);

      if (contents.sourceType === FRESH_SOURCE_ID) {
        const previous = freshShipments.find((picked) =>
          isSameShipment(picked, shipment),
        );
        const result = addFreshShipment(
          recordKeysBySource[FRESH_SOURCE_ID] ?? [],
          shipment,
          previous,
        );
        setRecordKeysBySource((prev) => ({
          ...prev,
          [FRESH_SOURCE_ID]: result.recordKeys,
        }));
        setFreshShipments((prev) => [
          ...prev.filter((picked) => !isSameShipment(picked, shipment)),
          result.shipment,
        ]);
        return {
          shipment: result.shipment,
          previous,
          addedKeys: result.added,
          rowKeys: result.added,
          alreadySelected: shipment.recordKeys.length - result.added.length,
        };
      }

      const known = new Set(frozenEmbryoPicks.map(toEmbryoPickKey));
      const added = toShipmentEmbryoPicks(contents).filter((pick) => {
        const key = toEmbryoPickKey(pick);
        if (known.has(key)) return false;
        known.add(key);
        return true;
      });
      setFrozenEmbryoPicks((prev) => addShipmentEmbryoPicks(prev, added));
      return {
        shipment,
        addedKeys: added.map(toEmbryoPickKey),
        rowKeys: [...new Set(added.map(toEmbryoPickMatingKey))],
        alreadySelected: shipment.embryoKeys.length - added.length,
      };
    },
    [freshShipments, frozenEmbryoPicks, recordKeysBySource],
  );

  const handleUndoShipment = useCallback((result: ShipmentAddResult) => {
    const added = new Set(result.addedKeys);
    if (result.shipment.sourceType === FRESH_SOURCE_ID) {
      setRecordKeysBySource((prev) => ({
        ...prev,
        [FRESH_SOURCE_ID]: (prev[FRESH_SOURCE_ID] ?? []).filter(
          (key) => !added.has(key),
        ),
      }));
      setFreshShipments((prev) =>
        result.previous
          ? prev.map((picked) =>
              isSameShipment(picked, result.shipment)
                ? (result.previous as PickedShipment)
                : picked,
            )
          : prev.filter((picked) => !isSameShipment(picked, result.shipment)),
      );
      return;
    }
    setFrozenEmbryoPicks((prev) =>
      prev.filter((pick) => !added.has(toEmbryoPickKey(pick))),
    );
  }, []);

  const handleRemoveShipment = useCallback(
    (shipment: PickedShipment) => {
      if (shipment.sourceType === FRESH_SOURCE_ID) {
        const current =
          freshShipments.find((picked) => isSameShipment(picked, shipment)) ??
          shipment;
        const result = removeFreshShipment(
          recordKeysBySource[FRESH_SOURCE_ID] ?? [],
          current,
          freshShipments.filter((picked) => !isSameShipment(picked, shipment)),
        );
        setRecordKeysBySource((prev) => ({
          ...prev,
          [FRESH_SOURCE_ID]: result.recordKeys,
        }));
        setFreshShipments(result.shipments);
        return;
      }
      setFrozenEmbryoPicks((prev) =>
        removeShipmentEmbryoPicks(
          prev,
          shipment,
          buildFrozenShipmentsFromPicks(prev).filter(
            (picked) => !isSameShipment(picked, shipment),
          ),
        ),
      );
    },
    [freshShipments, recordKeysBySource],
  );

  const handleReorder = useCallback((reordered: LoadedSource[]) => {
    setHasCustomOrder(true);
    setSourceOrder(reordered.map((s) => s.id));
  }, []);

  const handleRecordKeysChange = useCallback(
    (sourceId: string, recordKeys: string[]) => {
      setRecordKeysBySource((prev) => ({ ...prev, [sourceId]: recordKeys }));
    },
    [],
  );

  const buildSaveRequest = useCallback(
    (): SaveSelectionRequest => ({
      sources: orderedSourceIds.map((sourceId, index) => ({
        sourceType: toApiSourceType(sourceId),
        sequenceOrder: index + 1,
        records: (recordKeysBySource[sourceId] ?? []).map(fromRecordKey),
        ...(sourceId === FROZEN_SOURCE_ID
          ? {
              embryos: frozenEmbryoPicks.map((pick) => ({
                recordId: pick.embryoId,
                accountId: pick.accountId,
              })),
            }
          : {}),
      })),
    }),
    [frozenEmbryoPicks, orderedSourceIds, recordKeysBySource],
  );

  const canStart = useMemo(() => {
    if (isReadOnly) return false;
    if (orderedSourceIds.length === 0) return false;
    return orderedSourceIds.every(
      (sourceId) =>
        sourceId === MANUAL_ENTRY_SOURCE_ID ||
        (recordKeysBySource[sourceId]?.length ?? 0) > 0 ||
        (sourceId === FROZEN_SOURCE_ID && frozenEmbryoPicks.length > 0),
    );
  }, [frozenEmbryoPicks, isReadOnly, orderedSourceIds, recordKeysBySource]);

  const problemDetail = useCallback((error: unknown) => {
    if (error instanceof OnFarmImplantRequestError && error.problems.length) {
      return error.problems.join(' ');
    }
    return undefined;
  }, []);

  const handleSaveDraft = useCallback(async () => {
    if (!sessionsEnabled || !sessionId) return;
    try {
      await saveSelection({ sessionId, input: buildSaveRequest() });
      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.entry.saveDraftSuccess'),
      });
    } catch (error) {
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.entry.saveDraftFailed'),
        description: problemDetail(error),
      });
    }
  }, [
    buildSaveRequest,
    problemDetail,
    saveSelection,
    sessionId,
    sessionsEnabled,
    t,
  ]);

  const handleClearAll = useCallback(() => {
    confirm({
      title: t('onFarmImplant.entry.clearAllTitle'),
      description: t('onFarmImplant.entry.clearAllBody'),
      confirmLabel: t('onFarmImplant.entry.clearAll'),
      confirmVariant: 'destructive',
      onConfirm: () => {
        const resetLocalState = () => {
          setSelectedSourceIds([]);
          setSourceOrder([]);
          setRecordKeysBySource({});
          setHasCustomOrder(false);
          setFrozenEmbryoPicks([]);
          setFreshShipments([]);
        };

        if (!sessionsEnabled || !sessionId) {
          resetLocalState();
          return;
        }

        clearSelection(
          { sessionId },
          {
            onSuccess: resetLocalState,
            onError: () => {
              toast.add({
                data: { variant: 'error' },
                title: t('onFarmImplant.entry.clearAllFailed'),
              });
            },
          },
        );
      },
    });
  }, [clearSelection, confirm, sessionId, sessionsEnabled, t]);

  const runStart = useCallback(async () => {
    const firstSourceId = orderedSourceIds[0];

    if (!sessionsEnabled || !sessionId) {
      if (firstSourceId) onNavigateToTab(firstSourceId as ImplantTab);
      return;
    }

    try {
      await saveSelection({ sessionId, input: buildSaveRequest() });
      await startSession({ sessionId });
      if (firstSourceId) onNavigateToTab(firstSourceId as ImplantTab);
    } catch (error) {
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.entry.startWorkFailed'),
        description: problemDetail(error),
      });
    }
  }, [
    buildSaveRequest,
    onNavigateToTab,
    orderedSourceIds,
    problemDetail,
    saveSelection,
    sessionId,
    sessionsEnabled,
    startSession,
    t,
  ]);

  const findOtherActiveSession = useCallback(async () => {
    if (!sessionsEnabled || !sessionId || !session) return undefined;
    try {
      const response = await queryClient.fetchQuery(
        onFarmImplantSessionsOptions(token, {
          status: 'Active',
          siteId: session.siteId,
          accountId: session.accountId ?? undefined,
          sessionDate: session.sessionDate,
          take: 5,
        }),
      );
      return response.items.find((item) => item.sessionId !== sessionId);
    } catch {
      return undefined;
    }
  }, [queryClient, session, sessionId, sessionsEnabled, token]);

  const handleStartWork = useCallback(async () => {
    const existing = await findOtherActiveSession();

    if (existing) {
      confirm({
        title: t('onFarmImplant.entry.activeSessionTitle'),
        description: t('onFarmImplant.entry.activeSessionBody', {
          sessionId: formatShortSessionId(existing.sessionId),
        }),
        confirmLabel: t('onFarmImplant.entry.continueAnyway'),
        cancelLabel: t('onFarmImplant.entry.openExisting'),
        confirmVariant: 'warning',
        onConfirm: () => {
          void runStart();
        },
        onCancel: () => {
          navigate(`/on-farm-implant/entry?sessionId=${existing.sessionId}`);
        },
      });
      return;
    }

    await runStart();
  }, [confirm, findOtherActiveSession, navigate, runStart, t]);

  const renderCard = useCallback(
    (source: LoadedSource, index: number, onRemove: (id: string) => void) => (
      <LoadedSourceCard
        source={source}
        index={index}
        siteId={implantSiteId ?? ''}
        accountId={accountId}
        accountName={
          session?.accountId?.toLowerCase() === accountId?.toLowerCase()
            ? session?.accountName
            : undefined
        }
        sessionId={sessionId}
        selectedRecordKeys={recordKeysBySource[source.sourceId] ?? []}
        embryoPicks={
          source.sourceId === FROZEN_SOURCE_ID ? frozenEmbryoPicks : undefined
        }
        shipments={shipmentsBySource[source.sourceId]}
        onAddShipment={handleAddShipment}
        onUndoShipment={handleUndoShipment}
        onRemoveShipment={handleRemoveShipment}
        onSelectedRecordKeysChange={handleRecordKeysChange}
        readOnly={isReadOnly}
        onRemove={onRemove}
      />
    ),
    [
      accountId,
      frozenEmbryoPicks,
      handleAddShipment,
      handleRecordKeysChange,
      handleRemoveShipment,
      handleUndoShipment,
      implantSiteId,
      isReadOnly,
      recordKeysBySource,
      shipmentsBySource,
      session?.accountId,
      session?.accountName,
      sessionId,
    ],
  );

  const renderOptionBadge = useCallback(
    (option: EmbryoSource) => {
      if (option.availableCount === 0 && SHIPMENT_SOURCE_IDS.has(option.id)) {
        return (
          <span className="inline-block w-fit rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
            {t('onFarmImplant.sourceCard.shipments.byShipmentOnly')}
          </span>
        );
      }
      if (!option.manualEntry && option.availableCount === 0) {
        return (
          <span className="inline-block w-fit rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
            {t('onFarmImplant.sourceCard.unavailable')}
          </span>
        );
      }
      if (option.availableCount !== null) {
        return (
          <span className="inline-block w-fit rounded bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
            {t('onFarmImplant.available', { count: option.availableCount })}
          </span>
        );
      }
      if (option.manualEntry) {
        return (
          <span className="text-xs font-medium text-blue-600">
            {t('onFarmImplant.manualEntry')}
          </span>
        );
      }
      return null;
    },
    [t],
  );

  return (
    <>
      <div className="flex flex-1 flex-col gap-6 p-6">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            {t('onFarmImplant.entry.title')}
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            {t('onFarmImplant.entry.subtitle')}
          </p>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm font-medium text-gray-700">
            <div className="flex items-center gap-2">
              <i className="pi pi-building text-sm" />
              {t('onFarmImplant.entry.sessionContext')}
            </div>
            {sessionsEnabled && session && (
              <div className="flex flex-wrap items-center gap-4 text-xs font-normal text-gray-500">
                <span>
                  {t('onFarmImplant.entry.sessionLabel')}{' '}
                  <span
                    className="font-mono text-gray-700"
                    title={session.sessionId}
                  >
                    {formatShortSessionId(session.sessionId)}
                  </span>
                </span>
                <span>
                  {t('onFarmImplant.entry.sessionDate')}{' '}
                  <span className="text-gray-700">
                    {dateOnlyFormatter({ value: session.sessionDate })}
                  </span>
                </span>
                <span>
                  {t('onFarmImplant.entry.tripLabel')}{' '}
                  <span className="text-gray-700">
                    {session.tripNumber ?? '—'}
                  </span>
                </span>
                <StatusBadge label={session.status} colorMap={statusColors} />
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <SiteSelect
                label={t('onFarmImplant.entry.implantSite')}
                placeholder={t('onFarmImplant.entry.selectSite')}
                filter="aspiration"
                urlSync={false}
                value={implantSiteId}
                onChange={onSiteChange}
                allowClear={false}
                disabled={isReadOnly}
                triggerClassName="w-full"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm">
                {t('onFarmImplant.entry.accountLabel')}{' '}
                <span className="text-gray-400">
                  {t('onFarmImplant.entry.optional')}
                </span>
              </label>
              <AccountsCombobox
                value={accountId}
                onChange={onAccountChange}
                disabled={isReadOnly}
                placeholder={t('onFarmImplant.entry.searchAccountPlaceholder')}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {isLoading && implantSiteId && (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <i className="pi pi-spin pi-spinner text-sm" />
            {t('onFarmImplant.entry.loadingSources')}
          </div>
        )}

        <SelectableCardGrid<EmbryoSource, LoadedSource>
          options={embryoSources}
          selectedIds={selectedSourceIds}
          disabledIds={disabledSourceIds}
          onSelectionChange={handleSelectionChange}
          items={visibleLoadedSources}
          onReorder={handleReorder}
          renderItem={renderCard}
          renderOptionBadge={renderOptionBadge}
          sectionTitle={t('onFarmImplant.entry.selectEmbryoSources')}
          sectionSubtitle={t('onFarmImplant.entry.selectEmbryoSourcesSubtitle')}
          itemsTitle={t('onFarmImplant.entry.loadedWorkSequence')}
          itemsSubtitle={t('onFarmImplant.entry.loadedWorkSequenceSubtitle')}
          emptyIcon={<span>🧫</span>}
          emptyTitle={t('onFarmImplant.entry.noSourcesSelected')}
          emptySubtitle={t('onFarmImplant.entry.noSourcesSelectedSubtitle')}
        />
      </div>

      <div className="sticky bottom-0 flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          {isReadOnly ? (
            <>
              <i className="pi pi-lock text-sm text-gray-400" />
              {t('onFarmImplant.entry.readOnlyNotice')}
            </>
          ) : (
            !canStart && (
              <>
                <i className="pi pi-info-circle text-sm text-amber-500" />
                {t('onFarmImplant.entry.selectAtLeastOne')}
              </>
            )
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            disabled={isReadOnly}
            onClick={handleClearAll}
          >
            {t('onFarmImplant.entry.clearAll')}
          </Button>
          <Button
            variant="outline"
            disabled={isReadOnly || isSaving}
            onClick={() => {
              void handleSaveDraft();
            }}
          >
            {t('onFarmImplant.entry.saveDraft')}
          </Button>
          <Button
            disabled={!canStart || isSaving || isStarting}
            onClick={() => {
              void handleStartWork();
            }}
          >
            {t('onFarmImplant.entry.startWork')}
          </Button>
        </div>
      </div>
    </>
  );
};

export const OnFarmImplantEntryPage = () => {
  const token = useAccessToken();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const sessionsEnabled = useFeatureFlag('On_Farm_Implant_Sessions');
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('sessionId');
  const activeTab = (searchParams.get('tab') as ImplantTab) || 'entry';

  const {
    data: session,
    isLoading: isSessionLoading,
    isError: isSessionError,
  } = useQuery({
    ...onFarmImplantSessionOptions(token, sessionId ?? ''),
    enabled: !!token && sessionsEnabled && !!sessionId,
  });

  const { data: selection, isLoading: isSelectionLoading } = useQuery({
    ...onFarmImplantSelectionOptions(token, sessionId ?? ''),
    enabled: !!token && sessionsEnabled && !!sessionId,
  });

  const initialSources = useMemo(
    () =>
      [...(selection?.sources ?? [])].sort(
        (a, b) => a.sequenceOrder - b.sequenceOrder,
      ),
    [selection],
  );

  const { mutate: updateSession } = useUpdateOnFarmImplantSession();

  useSessionEvents(sessionsEnabled && sessionId ? sessionId : undefined);

  const implantSiteId =
    searchParams.get('sId') ??
    (sessionsEnabled ? session?.siteId : null) ??
    null;
  const accountId =
    searchParams.get('aId') ??
    (sessionsEnabled ? session?.accountId : undefined) ??
    undefined;

  const implantTabs: WorkflowTab<ImplantTab>[] = useMemo(
    () =>
      TAB_KEYS.map((key) => ({
        key,
        label: t(TAB_LABEL_KEYS[key]),
        icon: TAB_ICONS[key],
      })),
    [t],
  );

  const setActiveTab = useCallback(
    (tab: ImplantTab) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab === 'entry') {
            next.delete('tab');
          } else {
            next.set('tab', tab);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const updateParam = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set(key, value);
          } else {
            next.delete(key);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const persistContext = useCallback(
    (input: Parameters<typeof updateSession>[0]['input']) => {
      if (!sessionsEnabled || !sessionId) return;
      updateSession(
        { sessionId, input: { ...input, versions: session?.versions } },
        {
          onError: (error) => {
            console.error('Failed to update session context:', error);
            toast.add({
              data: { variant: 'error' },
              title: t('onFarmImplant.entry.contextSaveFailed'),
            });
          },
        },
      );
    },
    [session?.versions, sessionId, sessionsEnabled, t, updateSession],
  );

  const handleSiteChange = useCallback(
    (siteId: string | null) => {
      if (!siteId) return;
      updateParam('sId', siteId);
      persistContext({ siteId });
    },
    [persistContext, updateParam],
  );

  const handleAccountChange = useCallback(
    (id: string | undefined) => {
      updateParam('aId', id ?? null);
      persistContext(id ? { accountId: id } : { clearAccount: true });
    },
    [persistContext, updateParam],
  );

  const activeTabLabel =
    implantTabs.find((tab) => tab.key === activeTab)?.label ?? activeTab;
  const freshSource = initialSources.find(
    (source) => source.sourceType === 'fresh',
  );

  if (sessionsEnabled && !sessionId) {
    return <Navigate to="/on-farm-implant/sessions" replace />;
  }

  if (sessionsEnabled && (isSessionLoading || isSelectionLoading)) {
    return (
      <div className="flex flex-1 items-center justify-center gap-2 py-24 text-sm text-gray-500">
        <i className="pi pi-spin pi-spinner text-sm" />
        {t('onFarmImplant.entry.loadingSession')}
      </div>
    );
  }

  if (sessionsEnabled && (isSessionError || !session)) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-24 text-gray-500">
        <i className="pi pi-exclamation-triangle text-3xl text-amber-500" />
        <p className="text-sm">{t('onFarmImplant.entry.sessionNotFound')}</p>
        <Button onClick={() => navigate('/on-farm-implant/sessions')}>
          {t('onFarmImplant.entry.backToSessions')}
        </Button>
      </div>
    );
  }

  return (
    <ConfirmProvider>
      <div className="flex min-h-full flex-col">
        <WorkflowTabBar<ImplantTab>
          tabs={implantTabs}
          activeTab={activeTab}
          onTabChange={setActiveTab}
        />

        {activeTab === 'entry' ? (
          <EntryPageContent
            key={sessionId ?? 'legacy'}
            sessionsEnabled={sessionsEnabled}
            sessionId={sessionId}
            session={sessionsEnabled ? session : undefined}
            implantSiteId={implantSiteId}
            accountId={accountId}
            initialSources={initialSources}
            onSiteChange={handleSiteChange}
            onAccountChange={handleAccountChange}
            onNavigateToTab={setActiveTab}
          />
        ) : activeTab === 'fresh' && sessionsEnabled ? (
          <FreshTab
            sessionId={sessionId}
            records={freshSource?.records ?? []}
            readOnly={session?.status === 'Completed'}
          />
        ) : activeTab === 'frozen' && sessionsEnabled ? (
          <FrozenTab
            sessionId={sessionId}
            readOnly={session?.status === 'Completed'}
          />
        ) : activeTab === 'client-on-farm-tog' && sessionsEnabled ? (
          <ClientOnFarmTab
            key={sessionId ?? 'no-session'}
            sessionId={sessionId}
            readOnly={session?.status === 'Completed'}
          />
        ) : activeTab === 'non-tog' && sessionsEnabled ? (
          <NonTogTab
            key={sessionId ?? 'no-session'}
            sessionId={sessionId}
            session={session}
            readOnly={session?.status === 'Completed'}
          />
        ) : activeTab === 'implant-queue' && sessionsEnabled ? (
          <ImplantQueueTab
            sessionId={sessionId}
            readOnly={session?.status === 'Completed'}
          />
        ) : activeTab === 'on-farm-freezing' && sessionsEnabled ? (
          <OnFarmFreezingTab
            key={sessionId ?? 'no-session'}
            sessionId={sessionId}
            readOnly={session?.status === 'Completed'}
          />
        ) : activeTab === 'on-farm-preg-check' && sessionsEnabled ? (
          <PregCheckTab sessionId={sessionId} />
        ) : activeTab === 'qc' && sessionsEnabled ? (
          <QcTab sessionId={sessionId} />
        ) : (
          <PlaceholderTab label={activeTabLabel} />
        )}
      </div>
    </ConfirmProvider>
  );
};
